import type { PropertyDetails } from "@/src/constants/propertyWizard";
import type { PropertyContextItem, PropertyListItem } from "@/src/types/property.types";
import type { PropertyDto, UpdatePropertyPayload } from "@/src/types/propertyApi.types";
import {
  formatPropertyTypeForDisplay,
  mapPropertyTypeToApi,
} from "@/src/utils/mapPropertyTypeToApi";

export type PropertyEditForm = {
  name: string;
  addressLine: string;
  type: string;
  yearBuilt: string;
  lotSize: string;
};

export const normalizePropertyListData = (
  data: PropertyDto | PropertyDto[],
): PropertyDto[] => (Array.isArray(data) ? data : [data]);

export const resolvePropertyImageUrls = (dto: Pick<PropertyDto, "imageUrls" | "imageUrl">): string[] => {
  if (Array.isArray(dto.imageUrls) && dto.imageUrls.length > 0) {
    return dto.imageUrls.filter((url): url is string => Boolean(url));
  }
  if (dto.imageUrl) {
    return [dto.imageUrl];
  }
  return [];
};

const resolveHealthScore = (dto: PropertyDto): number | null => {
  if (typeof dto.homeHealthScore === "number") {
    return dto.homeHealthScore;
  }
  if (typeof dto.healthScore === "number") {
    return dto.healthScore;
  }
  return null;
};

export const mapPropertyDtoToListItem = (
  dto: PropertyDto,
  index: number,
): PropertyListItem => ({
  id: dto.id,
  name: dto.name,
  address: dto.addressLine,
  rooms: dto.roomCount ?? 0,
  lotSize: dto.lotSize?.trim() ?? "—",
  assets: dto.assetCount ?? 0,
  healthScore: resolveHealthScore(dto),
  isActive: dto.isActive,
  primary: dto.isPrimary ?? index === 0,
  imageStorageKeys: dto.imageStorageKeys ?? [],
  imageUrls: resolvePropertyImageUrls(dto),
  imageUrl: dto.imageUrl ?? resolvePropertyImageUrls(dto)[0] ?? null,
  relationship: dto.relationship ?? null,
});

export const mapPropertyDtosToListItems = (dtos: PropertyDto[]): PropertyListItem[] =>
  dtos.map((dto, index) => mapPropertyDtoToListItem(dto, index));

const parseYearBuilt = (value: string): number | undefined => {
  const parsed = Number.parseInt(value.trim(), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }
  return parsed;
};

const resolveLotSizeString = (lotSize: PropertyDto["lotSize"]): string => {
  if (typeof lotSize === "string") {
    return lotSize;
  }
  if (lotSize === null || lotSize === undefined) {
    return "";
  }
  return String(lotSize);
};

const resolveYearBuiltString = (yearBuilt: PropertyDto["yearBuilt"]): string => {
  if (typeof yearBuilt === "number" && Number.isFinite(yearBuilt)) {
    return String(yearBuilt);
  }
  return "";
};

export const mapPropertyDtoToEditForm = (dto: PropertyDto): PropertyEditForm => ({
  name: dto.name,
  addressLine: dto.addressLine,
  type: formatPropertyTypeForDisplay(dto.propertyType),
  yearBuilt: resolveYearBuiltString(dto.yearBuilt),
  lotSize: resolveLotSizeString(dto.lotSize),
});

export const propertyDetailsToEditForm = (details: PropertyDetails): PropertyEditForm => ({
  name: details.name,
  addressLine: details.addressLine,
  type: details.type,
  yearBuilt: details.yearBuilt,
  lotSize: details.lotSize,
});

export const isPropertyEditFormValid = (form: PropertyEditForm): boolean =>
  form.name.trim().length > 0 &&
  form.addressLine.trim().length > 0 &&
  form.type.trim().length > 0;

export const buildUpdatePropertyPayload = (form: PropertyEditForm): UpdatePropertyPayload => {
  const payload: UpdatePropertyPayload = {
    name: form.name.trim(),
    addressLine: form.addressLine.trim(),
    propertyType: mapPropertyTypeToApi(form.type),
  };

  const yearBuilt = parseYearBuilt(form.yearBuilt);
  if (yearBuilt !== undefined) {
    payload.yearBuilt = yearBuilt;
  }

  const lotSize = form.lotSize.trim();
  if (lotSize) {
    payload.lotSize = lotSize;
  }

  return payload;
};

export const mapListItemToContextItem = (item: PropertyListItem): PropertyContextItem => ({
  ...item,
  alerts: 0,
});
